import { useEffect, useState } from 'react';
import { KeyOutlined, MessageOutlined, SendOutlined, WalletOutlined } from '@ant-design/icons';
import { Alert, App, Button, Form, Input, Popconfirm, Radio, Switch, Tag } from 'antd';
import { api } from '../../api';
import { ErrorNotice, FormSkeleton, PageHeader, Panel, StatCard } from '../../components';
import { timeAgo, useLoad } from '../../lib';
import { BackToSettings } from './BackToSettings';
const ROUTES = {
    otp: { label: 'OTP', detail: 'Fast2SMS OTP template (DLT approved). Recommended.' },
    dlt: { label: 'DLT', detail: 'Your own DLT sender ID and approved message template.' },
    quick: { label: 'Quick SMS', detail: 'Plain text, no DLT. Costs more; for testing or a fallback.' }
};
const FIELDS = ['enabled', 'route', 'otpTemplateId', 'senderId', 'dltMessageId', 'quickText'];
/**
 * Settings › SMS: the Fast2SMS key and route that every sign-in code goes out with (admin console, account panel and
 * the desktop app). Saved in the database; the key is encrypted and never shown again.
 */
export function SmsSettings() {
    const { message } = App.useApp();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [testing, setTesting] = useState(false);
    const [test, setTest] = useState(null);
    const [balance, setBalance] = useState(null);
    const [checking, setChecking] = useState(false);
    const { data, error, loading, reload } = useLoad(() => api('/v1/admin/settings/sms').then((body) => body.settings));
    const settings = data;
    const route = Form.useWatch('route', form) ?? settings?.route ?? 'otp';
    useEffect(() => {
        if (!settings)
            return;
        form.setFieldsValue({ ...Object.fromEntries(FIELDS.map((key) => [key, settings[key]])), apiKey: '', testMobile: form.getFieldValue('testMobile') ?? '' });
        setDirty(false);
    }, [settings, form]);
    const values = () => {
        const all = form.getFieldsValue();
        return { ...Object.fromEntries(FIELDS.map((key) => [key, all[key]])), apiKey: all.apiKey ?? '' };
    };
    async function save(clearKey = false) {
        setSaving(true);
        try {
            const body = await api('/v1/admin/settings/sms', { method: 'PUT', body: JSON.stringify({ ...values(), clearKey, ...(clearKey ? { enabled: false } : {}) }) });
            message.success(clearKey ? 'Key removed. Codes are not sent by SMS.' : body.settings.active ? 'Saved. Sign-in codes go out by SMS.' : 'Saved. SMS is off.');
            setTest(null);
            await reload();
        }
        catch (err) {
            message.error(err instanceof Error ? err.message : 'Could not save the settings');
        }
        finally {
            setSaving(false);
        }
    }
    async function sendTest() {
        const mobile = form.getFieldValue('testMobile') ?? '';
        setTesting(true);
        setTest(null);
        try {
            const result = await api('/v1/admin/settings/sms/test', { method: 'POST', body: JSON.stringify({ ...values(), mobile }) });
            setTest({ ok: true, text: `Fast2SMS accepted a test SMS to ${result.sentTo} with the code ${result.code}. Check that it arrived.` });
        }
        catch (err) {
            setTest({ ok: false, text: err instanceof Error ? err.message : 'The test SMS could not be sent' });
        }
        finally {
            setTesting(false);
        }
    }
    async function checkBalance() {
        setChecking(true);
        try {
            setBalance(await api('/v1/admin/settings/sms/balance', { method: 'POST', body: JSON.stringify({ apiKey: form.getFieldValue('apiKey') ?? '' }) }));
        }
        catch (err) {
            message.error(err instanceof Error ? err.message : 'The balance could not be read');
        }
        finally {
            setChecking(false);
        }
    }
    const on = Boolean(settings?.active);
    return (<>
      <PageHeader title="SMS" description="Sign-in codes for this admin console, the account panel and the desktop app are sent by SMS through Fast2SMS. Kivonex makes and checks the codes; Fast2SMS only delivers them." actions={<BackToSettings />}/>
      <ErrorNotice error={error}/>
      {settings?.keyUnreadable && (<Alert type="warning" showIcon title="The saved Fast2SMS key cannot be read on this server" description="The server's settings key was replaced or lost, so codes are not sent by SMS until the key is entered again." style={{ marginBottom: 20 }}/>)}
      {settings && settings.enabled && !on && (<Alert type="warning" showIcon title="SMS is switched on but not complete" description={settings.missing} style={{ marginBottom: 20 }}/>)}

      <div className="stats">
        <StatCard label="Sign-in codes" tone={on ? 'green' : 'slate'} icon={<MessageOutlined />} loading={loading && !data} value={on ? 'By SMS' : 'Off'} foot={settings?.updatedAt ? `Changed ${timeAgo(settings.updatedAt)}` : 'Not set up yet'}/>
        <StatCard label="Route" tone="indigo" icon={<SendOutlined />} loading={loading && !data} value={<span style={{ fontSize: 18 }}>{ROUTES[settings?.route ?? 'otp']?.label}</span>} foot={settings?.route === 'dlt' && settings.senderId ? `Sender ${settings.senderId}` : 'Fast2SMS'}/>
        <StatCard label="Fast2SMS wallet" tone="amber" icon={<WalletOutlined />} loading={loading && !data} value={balance ? `₹${balance.balance.toLocaleString('en-IN', { maximumFractionDigits: 2 })}` : '—'} foot={balance ? (balance.smsCount != null ? `About ${balance.smsCount.toLocaleString('en-IN')} SMS` : 'Balance') : (<Button size="small" type="link" style={{ padding: 0 }} loading={checking} onClick={() => void checkBalance()} disabled={!settings?.apiKeySet}>Check balance</Button>)}/>
      </div>

      {loading && !data ? (<Panel flush title="Fast2SMS" description="fast2sms.com › Dev API">
          <FormSkeleton sections={3}/>
        </Panel>) : (<Form form={form} layout="vertical" requiredMark={false} onFinish={() => void save()} onValuesChange={(changed) => {
                if (Object.keys(changed).some((key) => key !== 'testMobile'))
                    setDirty(true);
            }}>
        <Panel flush title="Fast2SMS" description="fast2sms.com › Dev API" extra={on ? <Tag color="success" variant="filled"><span className="status-dot"/>On</Tag> : <Tag variant="filled">Off</Tag>}>
          <div className="form-section">
            <div>
              <h4>Send codes by SMS</h4>
              <p>While this is off, codes are not sent by SMS (a development server writes them to its own log). Switch it on only after a test SMS arrives.</p>
            </div>
            <div>
              <Form.Item name="enabled" valuePropName="checked" label="Sign-in codes by SMS">
                <Switch />
              </Form.Item>
            </div>
          </div>

          <div className="form-section">
            <div>
              <h4>API key</h4>
              <p>Copy it from Fast2SMS › Dev API. It is stored encrypted and never shown again.</p>
            </div>
            <div>
              <Form.Item label="Fast2SMS API key" name="apiKey" extra={settings?.apiKeySet ? <span><KeyOutlined style={{ color: 'var(--brand)' }}/> A key ending <span className="mono">{settings.apiKeyHint}</span> is saved. Leave blank to keep it.</span> : 'Without a key, codes cannot be sent by SMS.'}>
                <Input.Password autoComplete="new-password" placeholder={settings?.apiKeySet ? '••••••••••••' : 'Fast2SMS API key'} className="mono"/>
              </Form.Item>
              {settings?.apiKeySet && (<Popconfirm title="Remove the saved key?" description="Sign-in codes stop going out by SMS until a key is added again." okText="Remove" okButtonProps={{ danger: true }} onConfirm={() => save(true)}>
                  <Button danger type="link" style={{ paddingInline: 0 }}>Remove key</Button>
                </Popconfirm>)}
            </div>
          </div>

          <div className="form-section">
            <div>
              <h4>Route</h4>
              <p>How Fast2SMS sends the message. Indian rules need a DLT-approved template for OTP messages.</p>
            </div>
            <div>
              <Form.Item name="route">
                <Radio.Group className="sms-routes">
                  {Object.entries(ROUTES).map(([value, item]) => (<Radio key={value} value={value}>
                      <b>{item.label}</b>
                      <div className="cell-sub">{item.detail}</div>
                    </Radio>))}
                </Radio.Group>
              </Form.Item>
              {route === 'otp' && (<Form.Item label="OTP template ID" name="otpTemplateId" extra="Fast2SMS › OTP › your approved template's ID.">
                  <Input className="mono" placeholder="e.g. 1234"/>
                </Form.Item>)}
              {route === 'dlt' && (<div className="form-row">
                  <Form.Item label="Sender ID" name="senderId" extra="3–6 letters, approved on DLT.">
                    <Input className="mono" maxLength={6} style={{ textTransform: 'uppercase' }} placeholder="KIVONX"/>
                  </Form.Item>
                  <Form.Item label="DLT message ID" name="dltMessageId" extra="Fast2SMS › DLT Manager › the template's message ID. Its one variable is the code.">
                    <Input className="mono" placeholder="e.g. 171234"/>
                  </Form.Item>
                </div>)}
              {route === 'quick' && (<Form.Item label="Message" name="quickText" extra="{code} is replaced by the 6-digit code.">
                  <Input.TextArea placeholder='Your Kivonex code is {code}' rows={3} maxLength={300} showCount/>
                </Form.Item>)}
            </div>
          </div>

          <div className="form-section">
            <div>
              <h4>Test</h4>
              <p>Sends one SMS with the settings above, saved or not. The test code signs nobody in.</p>
            </div>
            <div className="form-row">
              <Form.Item label="Mobile number" name="testMobile">
                <Input inputMode="numeric" maxLength={14} placeholder="98XXXXXXXX" className="mono"/>
              </Form.Item>
              <Form.Item label=" ">
                <Button icon={<SendOutlined />} onClick={() => void sendTest()} loading={testing}>Send test SMS</Button>
              </Form.Item>
            </div>
          </div>

          {test && (<div style={{ padding: '0 20px 20px' }}>
              <Alert type={test.ok ? 'success' : 'error'} showIcon title={test.text}/>
            </div>)}
          <div className="form-actions">
            <Button icon={<WalletOutlined />} onClick={() => void checkBalance()} loading={checking}>Check balance</Button>
            <Button type="primary" htmlType="submit" loading={saving} disabled={!dirty && Boolean(settings?.updatedAt)}>Save changes</Button>
          </div>
        </Panel>
      </Form>)}
    </>);
}
